import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {FilterKey, keyToEmptyFilter, Operator, SearchParams} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';

function getDefaultSearchSearchParamsWithProcessesSorts(): SearchParams {
  return {
    ...getDefaultSearchSearchParams(),
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROCESSES),
  };
}

function getModelFilteredByProcesses(
  modelType: ModelTypeConstants.PROCESS_TASK | ModelTypeConstants.PROCESS_OUTPUT,
  processIds: Array<string>,
  schemaId?: string
): SearchParams {
  assertAndContinue(processIds.length > 0, 'Expected at least one processId');
  return SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParamsWithProcessesSorts(), [
    {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
    {...keyToEmptyFilter(FilterKey.TYPES), value: [modelType]},
    ...(schemaId != null
      ? [
          {
            ...keyToEmptyFilter(FilterKey.SCHEMA),
            value: [schemaId],
          },
        ]
      : []),
    {...keyToEmptyFilter(FilterKey.PROCESS), value: processIds},
  ]);
}

function getFailedMappingProcessTasksFilteredByProcess(processId: string): SearchParams {
  return SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParamsWithProcessesSorts(), [
    {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
    {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.PROCESS_TASK]},
    {...keyToEmptyFilter(FilterKey.PROCESS), value: [processId]},
    {
      key: FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS,
      operator: Operator.IS,
      value: 'hasFailedMappings',
    },
  ]);
}

function getOutputsFilteredByTasks(taskIds: Array<string>): SearchParams {
  assertAndContinue(taskIds.length > 0, 'Expected at least one taskId');
  return SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParamsWithProcessesSorts(), [
    {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
    {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.PROCESS_OUTPUT]},
    {...keyToEmptyFilter(FilterKey.PROCESS_TASK), value: taskIds},
  ]);
}

function getModelFilteredByHumanIds(
  modelType: ModelTypeConstants.PROCESS_TASK | ModelTypeConstants.PROCESS_OUTPUT,
  humanIds: Array<string>
): SearchParams {
  assertAndContinue(humanIds.length > 0, 'Expected at least one humanId');
  return SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParamsWithProcessesSorts(), [
    {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
    {...keyToEmptyFilter(FilterKey.TYPES), value: [modelType]},
    {...keyToEmptyFilter(FilterKey.NAMES), value: humanIds},
  ]);
}

function getFilterErrorsArray(searchParams: SearchParams): Array<null> {
  return new Array(searchParams.filters.length).fill(null);
}

function getDashboardUrl(searchParams: SearchParams): string {
  return getNavigationURL('/workflows', searchParams, getFilterErrorsArray(searchParams));
}

export function getTasksFilteredByHumanIds(taskHumanIds: Array<string>): string {
  const searchParams = getModelFilteredByHumanIds(ModelTypeConstants.PROCESS_TASK, taskHumanIds);
  return getDashboardUrl(searchParams);
}

export function getTasksFilteredByProcessesUrl(processIds: Array<string>, schemaId?: string): string {
  const searchParams = getModelFilteredByProcesses(ModelTypeConstants.PROCESS_TASK, processIds, schemaId);
  return getDashboardUrl(searchParams);
}

export function getFailedMappingTasksFilteredByProcessesUrl(processId: string): string {
  const searchParams = getFailedMappingProcessTasksFilteredByProcess(processId);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByHumanIds(outputHumanIds: Array<string>): string {
  const searchParams = getModelFilteredByHumanIds(ModelTypeConstants.PROCESS_OUTPUT, outputHumanIds);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByProcessesUrl(processIds: Array<string>, schemaId?: string): string {
  const searchParams = getModelFilteredByProcesses(ModelTypeConstants.PROCESS_OUTPUT, processIds, schemaId);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByTasksUrl(taskIds: Array<string>): string {
  const searchParams = getOutputsFilteredByTasks(taskIds);
  return getDashboardUrl(searchParams);
}
